import type { CustomDataMap } from './subscriberCustomFields';
import type { SubscriberRow } from '../components/Subscribers/types';

/** One Calendly scheduled row (invitee + event), from edge function. */
export interface CalendlyMeeting {
  id: string;
  name: string;
  email: string;
  /** Event start time (ISO) — used for sort and last contact */
  lastContactAt: string;
  eventName: string;
  meetingStatus: 'active' | 'canceled' | 'completed';
  durationMinutes: number;
  calendlyEventUri?: string;
  calendlyInviteeUri?: string;
  /**
   * Calendly `questions_and_answers` only: key = camelCase(question text), value = answer string.
   */
  customAnswers?: Record<string, string>;
}

export const CALENDLY_SOURCE = 'calendly';
export const CALENDLY_CHANNEL = 'calendly';

function normalizeLinkedinAnswer(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/linkedin\.com/i.test(trimmed)) {
    return trimmed.startsWith('http') ? trimmed : `https://${trimmed.replace(/^\/+/, '')}`;
  }
  const pathy = trimmed.replace(/^\/+/, '');
  if (/^in\//i.test(pathy) || pathy.includes('/')) {
    return `https://www.linkedin.com/${pathy}`;
  }
  if (/^[a-zA-Z0-9_-]+$/.test(pathy)) {
    return `https://www.linkedin.com/in/${pathy}`;
  }
  return trimmed;
}

/** First `customAnswers` entry whose camelCase key contains `linkedin`. */
export function linkedinUrlFromCustomAnswers(
  customAnswers: Record<string, string> | undefined,
): string | undefined {
  if (!customAnswers) return undefined;
  for (const [key, raw] of Object.entries(customAnswers)) {
    if (!/linkedin/i.test(key)) continue;
    const url = normalizeLinkedinAnswer(raw ?? '');
    if (url) return url;
  }
  return undefined;
}

export function calendlyMeetingStatusClass(s: CalendlyMeeting['meetingStatus']): string {
  switch (s) {
    case 'active':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300';
    case 'completed':
      return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200';
    case 'canceled':
      return 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

export function calendlyStatusLabel(s: CalendlyMeeting['meetingStatus']): string {
  switch (s) {
    case 'active':
      return 'Scheduled';
    case 'completed':
      return 'Completed';
    case 'canceled':
      return 'Canceled';
    default:
      return s;
  }
}

/** Known custom field keys for Calendly import (first match wins at save time). */
export const CALENDLY_CUSTOM_FIELD_KEYS = {
  icp: ['icp'],
  linkedin_url: ['linkedin_url', 'linkedin', 'linked_url', 'linkedinurl'],
} as const;

/** Map a Calendly row into subscriber form fields for the add-subscriber modal. */
export function meetingToSubscriberDraft(meeting: CalendlyMeeting): Partial<SubscriberRow> {
  const linkedin = linkedinUrlFromCustomAnswers(meeting.customAnswers);
  const custom_data: CustomDataMap = {};
  if (linkedin) {
    custom_data.linkedin_url = linkedin;
  }
  custom_data.icp = `${meeting.durationMinutes} min`;

  return {
    email: meeting.email,
    name: meeting.name,
    role: meeting.eventName,
    last_contacted_at: meeting.lastContactAt,
    source: CALENDLY_SOURCE,
    funnel_status: 'prospect',
    email_status: 'raw',
    custom_data,
    channels: { [CALENDLY_CHANNEL]: true },
  };
}

/** @deprecated Connections import — re-export alias */
export function meetingToConnectionDraft(meeting: CalendlyMeeting): {
  full_name: string;
  email_address: string;
  linkedin_url?: string;
  last_contact_at: string;
  role: string;
  icp: string;
} {
  return {
    full_name: meeting.name,
    email_address: meeting.email,
    linkedin_url: linkedinUrlFromCustomAnswers(meeting.customAnswers),
    last_contact_at: meeting.lastContactAt,
    role: meeting.eventName,
    icp: `${meeting.durationMinutes} min`,
  };
}
